import { useReducer, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { GetPickingResponse, ScanRequest, ScannedBagInput } from "@pm-barcode/shared";
import { useApiClient } from "../../hooks/use-api-client.ts";
import { useScanner } from "../../hooks/use-scanner.ts";
import { bagTotal, useLeaveGuard } from "../../hooks/use-leave-guard.ts";
import type { ManualScanResponse } from "@pm-barcode/shared";
import { RequestError, type LocationSummary } from "../../api/client.ts";
import { pickingStateLabel } from "../../lib/picking-state.ts";
import { ManualBagForm } from "../../components/manual-bag-form.tsx";
import { BagCountPrompt } from "../../components/bag-count-prompt.tsx";
import { allocateToLines, productTotals } from "../../lib/receipt-lines.ts";

/** A scanned non-serial bag awaiting a bag-count entry. */
interface PendingCount {
  productId: number;
  productName: string;
  lotId: number;
  lotName: string;
  moveId: number;
  sourceLocationId: number;
  destLocationId: number;
  tracking: string;
  batch: string;
  bagNumber?: string;
  weightKg: number;
}
import { useSettings } from "../../context/settings-context.tsx";

// ─── Audio / Haptic ─────────────────────────────────────────────────────────

function vibrate(pattern: number | number[]): void {
  if (!("vibrate" in navigator)) return;
  navigator.vibrate(pattern);
}

let audioCtx: AudioContext | null = null;

function beep(type: "success" | "error"): void {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const play = () => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.frequency.value = type === "success" ? 880 : 220;
      const now = audioCtx!.currentTime;
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    };
    // WebView AudioContext may start suspended when triggered from native code.
    if (audioCtx.state === "suspended") {
      void audioCtx.resume().then(play);
    } else {
      play();
    }
  } catch {
    // Ignore if audio is unavailable
  }
}

// ─── State machine ──────────────────────────────────────────────────────────

type Feedback =
  | { kind: "ok"; productName: string; weightKg: number; batch: string; bagNumber?: string }
  | { kind: "err"; title: string };

type Modal =
  | { kind: "unknown"; rawBarcode: string }
  | { kind: "not_on_picking"; category: string; batch: string; suggestion?: { productId: number; productName: string } }
  | { kind: "already_scanned"; lotName: string }
  | { kind: "already_received"; lotName: string }
  | { kind: "variance"; expected: number; actual: number }
  | { kind: "error"; title: string; message: string };

interface State {
  phase: "loading" | "load_error" | "ready" | "validating" | "done";
  picking: GetPickingResponse | null;
  errorMsg: string | null;
  /**
   * Quantity scanned this session, by receipt line (move id) — not by product, which counted a
   * bag once per line when a product had two (see lib/receipt-lines.ts).
   */
  localQty: Record<number, number>;
  /** Bags scanned this session — passed to the validate endpoint for deferred commit. */
  scannedBags: ScannedBagInput[];
  /** Overridden receiving location (null = use the picking's default destination). */
  destLocation: { id: number; name: string } | null;
  feedback: Feedback | null;
  modal: Modal | null;
  scanSeq: number;
  backorder: { created: boolean; ref?: string } | null;
}

type Action =
  | { type: "LOADED"; picking: GetPickingResponse }
  | { type: "LOAD_FAIL"; msg: string }
  | { type: "SCAN_OK"; productName: string; weightKg: number; batch: string; bagNumber?: string; bags: ScannedBagInput[] }
  | { type: "SCAN_UNKNOWN"; rawBarcode: string }
  | { type: "SCAN_NOT_ON_PICKING"; category: string; batch: string; suggestion?: { productId: number; productName: string } }
  | { type: "SCAN_ALREADY_SCANNED"; lotName: string }
  | { type: "SCAN_ALREADY_RECEIVED"; lotName: string }
  | { type: "SCAN_VARIANCE"; expected: number; actual: number; bags: ScannedBagInput[] }
  | { type: "SCAN_ERR"; title: string; msg: string }
  | { type: "SET_LOCATION"; location: { id: number; name: string } }
  | { type: "DISMISS" }
  | { type: "VALIDATE_START" }
  | { type: "VALIDATE_OK"; backorderCreated: boolean; backorderRef?: string }
  | { type: "VALIDATE_FAIL"; msg: string };

const initialState: State = {
  phase: "loading",
  picking: null,
  errorMsg: null,
  localQty: {},
  scannedBags: [],
  destLocation: null,
  feedback: null,
  modal: null,
  scanSeq: 0,
  backorder: null,
};

/** Add scanned bags to the per-line session counts. */
function addToLines(localQty: Record<number, number>, bags: readonly ScannedBagInput[]): Record<number, number> {
  const next = { ...localQty };
  for (const bag of bags) {
    next[bag.moveId] = (next[bag.moveId] ?? 0) + bag.quantity;
  }
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "LOADED":
      return { ...state, phase: "ready", picking: action.picking };
    case "LOAD_FAIL":
      return { ...state, phase: "load_error", errorMsg: action.msg };
    case "SCAN_OK":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: {
          kind: "ok",
          productName: action.productName,
          weightKg: action.weightKg,
          batch: action.batch,
          bagNumber: action.bagNumber,
        },
        localQty: addToLines(state.localQty, action.bags),
        scannedBags: [...state.scannedBags, ...action.bags],
        modal: null,
      };
    case "SCAN_VARIANCE":
      // Over-receipt bag: warn but still track for commit at validate time.
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Extra bag" },
        localQty: addToLines(state.localQty, action.bags),
        scannedBags: [...state.scannedBags, ...action.bags],
        modal: { kind: "variance", expected: action.expected, actual: action.actual },
      };
    case "SCAN_UNKNOWN":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Unknown barcode" },
        modal: { kind: "unknown", rawBarcode: action.rawBarcode },
      };
    case "SCAN_NOT_ON_PICKING":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Not on picking" },
        modal: { kind: "not_on_picking", category: action.category, batch: action.batch, suggestion: action.suggestion },
      };
    case "SCAN_ALREADY_SCANNED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already scanned" },
        modal: { kind: "already_scanned", lotName: action.lotName },
      };
    case "SCAN_ALREADY_RECEIVED":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: "Already received" },
        modal: { kind: "already_received", lotName: action.lotName },
      };
    case "SCAN_ERR":
      return {
        ...state,
        scanSeq: state.scanSeq + 1,
        feedback: { kind: "err", title: action.title },
        modal: { kind: "error", title: action.title, message: action.msg },
      };
    case "SET_LOCATION":
      // Override the receiving location for the whole receipt: apply it to bags already
      // scanned this session as well as future ones, so all stock lands in the chosen place.
      return {
        ...state,
        destLocation: action.location,
        scannedBags: state.scannedBags.map((b) => ({ ...b, destLocationId: action.location.id })),
      };
    case "DISMISS":
      return { ...state, modal: null };
    case "VALIDATE_START":
      return { ...state, phase: "validating", errorMsg: null };
    case "VALIDATE_OK":
      return {
        ...state,
        phase: "done",
        backorder: { created: action.backorderCreated, ref: action.backorderRef },
      };
    case "VALIDATE_FAIL":
      return { ...state, phase: "ready", errorMsg: action.msg };
    default:
      return state;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ReceiptPicking() {
  const navigate = useNavigate();
  const api = useApiClient();
  const { hapticEnabled, setHapticEnabled } = useSettings();
  const [state, dispatch] = useReducer(reducer, initialState);
  const scanInFlight = useRef(false);
  // Ref-based dedup: updated synchronously on accept, no closure-timing gap.
  const scannedSerialIdsRef = useRef<Set<number>>(new Set());
  const [showStaleWarning, setShowStaleWarning] = useState(false);
  const loadedAtRef = useRef<number | null>(null);

  const [showManual, setShowManual] = useState(false);
  const [pendingCount, setPendingCount] = useState<PendingCount | null>(null);

  /**
   * Put a scanned bag (or counted lot entry) on the right receipt line(s). The Worker names the
   * product's first line; with two lines for one product that sent every bag to the first, so
   * the screen allocates using this session's own per-line counts.
   */
  function toLineBags(bag: ScannedBagInput): ScannedBagInput[] {
    const shares = allocateToLines(state.picking?.moves ?? [], state.localQty, bag.productId, bag.quantity);
    return shares.length === 0
      ? [bag]
      : shares.map((share) => ({ ...bag, moveId: share.moveId, quantity: share.quantity }));
  }

  // Confirm the bag count for a scanned non-serial (lot) product and record it as one entry.
  function confirmCount(count: number): void {
    const p = pendingCount;
    if (!p) return;
    const bag: ScannedBagInput = {
      lotId: p.lotId,
      lotName: p.lotName,
      batch: p.batch,
      quantity: count,
      moveId: p.moveId,
      sourceLocationId: p.sourceLocationId,
      destLocationId: state.destLocation?.id ?? p.destLocationId,
      productId: p.productId,
      tracking: p.tracking,
    };
    const bags = toLineBags(bag);
    const totals = productTotals(state.picking?.moves ?? [], state.localQty, p.productId);
    const newQty = totals.received + count;
    if (hapticEnabled) vibrate(200);
    if (totals.expected > 0 && newQty > totals.expected) {
      dispatch({ type: "SCAN_VARIANCE", expected: totals.expected, actual: newQty, bags });
    } else {
      dispatch({
        type: "SCAN_OK",
        productName: p.productName,
        weightKg: p.weightKg * count, // total weight of the counted bags
        batch: p.batch,
        bagNumber: p.bagNumber,
        bags,
      });
    }
    setPendingCount(null);
  }

  // Add a damaged-barcode bag recorded via /scan/manual to the session, like a scan.
  function onManualAdded(result: ManualScanResponse): void {
    const bag: ScannedBagInput = {
      lotId: result.odoo.lotId,
      lotName: result.odoo.lotName,
      batch: result.batch,
      quantity: result.odoo.qtyDone,
      moveId: result.odoo.moveId,
      sourceLocationId: result.odoo.sourceLocationId,
      destLocationId: state.destLocation?.id ?? result.odoo.destLocationId,
      productId: result.odoo.productId,
      tracking: result.odoo.tracking,
    };
    if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
    beep("success");
    if (hapticEnabled) vibrate(200);
    dispatch({
      type: "SCAN_OK",
      productName: result.odoo.productName,
      weightKg: result.weightKg,
      batch: result.batch,
      bagNumber: result.bagNumber,
      bags: toLineBags(bag),
    });
    setShowManual(false);
  }

  // "Change Location" picker — scan a location barcode or pick from the warehouse list.
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locations, setLocations] = useState<LocationSummary[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  async function loadLocations(): Promise<void> {
    // Always refetch when the picker opens so newly-created Odoo locations show up without
    // needing an app restart.
    if (!api) return;
    setLocationsLoading(true);
    try {
      const res = await api.listLocations();
      setLocations(res.locations);
    } catch {
      setLocationError("Could not load the location list — try again, or scan a location barcode.");
    } finally {
      setLocationsLoading(false);
    }
  }

  // useParams: at runtime we're on route "/receipt/$ref"; ref is the picking reference
  const { ref } = useParams({ strict: false }) as { ref: string };

  // Load the picking on mount
  useEffect(() => {
    if (!api || !ref) return;
    let cancelled = false;
    api
      .getPicking(ref)
      .then((picking) => {
        if (!cancelled) dispatch({ type: "LOADED", picking });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          dispatch({
            type: "LOAD_FAIL",
            msg: err instanceof Error ? err.message : "Failed to load picking",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, ref]);

  // Capture load timestamp for stale-data warning.
  useEffect(() => {
    if (state.phase === "ready" && loadedAtRef.current === null) {
      loadedAtRef.current = Date.now();
    }
  }, [state.phase]);

  // Show stale warning after 20 minutes on the same picking.
  useEffect(() => {
    if (state.phase !== "ready") return;
    const interval = setInterval(() => {
      if (loadedAtRef.current && Date.now() - loadedAtRef.current >= 20 * 60 * 1000) {
        setShowStaleWarning(true);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [state.phase]);

  // Confirm before leaving (← button, hardware back, reload) while there are unsaved scans.
  const leave = useLeaveGuard(
    (state.phase === "ready" || state.phase === "validating") && state.scannedBags.length > 0,
  );

  // Scan handler
  useScanner((event) => {
    // Location-change mode: a scan picks the receiving location (matched against the loaded
    // warehouse list by barcode) instead of being treated as a bag.
    if (showLocationPicker) {
      const loc = locations.find((l) => l.barcode === event.rawBarcode);
      if (loc) {
        beep("success");
        if (hapticEnabled) vibrate(100);
        dispatch({ type: "SET_LOCATION", location: { id: loc.id, name: loc.name } });
        setShowLocationPicker(false);
        setLocationError(null);
      } else {
        beep("error");
        if (hapticEnabled) vibrate([100, 50, 100]);
        setLocationError(`"${event.rawBarcode}" is not a location in this warehouse.`);
      }
      return;
    }

    if (state.phase !== "ready" || !api || scanInFlight.current || pendingCount) return;
    scanInFlight.current = true;

    const body: ScanRequest = {
      rawBarcode: event.rawBarcode,
      symbology: event.symbology,
      workflow: "receipt",
      pickingRef: ref,
      deviceId: import.meta.env.VITE_DEVICE_ID,
      capturedAt: event.capturedAt,
    };

    void api
      .postScan(body)
      .then((result) => {
        if (result.outcome === "ok") {
          // Non-serial (lot) products: prompt for the number of bags instead of re-scanning
          // the same barcode. The bag is recorded when the operator confirms the count.
          if (result.odoo.tracking !== "serial") {
            beep("success");
            if (hapticEnabled) vibrate(120);
            setPendingCount({
              productId: result.odoo.productId,
              productName: result.odoo.productName,
              lotId: result.odoo.lotId,
              lotName: result.odoo.lotName,
              moveId: result.odoo.moveId,
              sourceLocationId: result.odoo.sourceLocationId,
              destLocationId: result.odoo.destLocationId,
              tracking: result.odoo.tracking,
              batch: result.normalised.batch,
              bagNumber: result.normalised.bagNumber,
              weightKg: result.normalised.weightKg,
            });
            return;
          }

          const bag: ScannedBagInput = {
            lotId: result.odoo.lotId,
            lotName: result.odoo.lotName,
            batch: result.normalised.batch,
            quantity: result.odoo.qtyDone, // 1 unit per bag — the kg weight is display-only
            moveId: result.odoo.moveId,
            sourceLocationId: result.odoo.sourceLocationId,
            // Receive into the operator-chosen location if set, else the picking's default.
            destLocationId: state.destLocation?.id ?? result.odoo.destLocationId,
            productId: result.odoo.productId,
            tracking: result.odoo.tracking,
          };

          // Duplicate detection for serial products — ref avoids any closure-timing gap.
          if (result.odoo.tracking === "serial" && scannedSerialIdsRef.current.has(result.odoo.lotId)) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            dispatch({ type: "SCAN_ALREADY_SCANNED", lotName: result.odoo.lotName });
            return;
          }

          // Client-side over-receipt check, against everything expected of this product across
          // all its lines — one line's quantity warned "extra bag" far too early on P00026.
          const bags = toLineBags(bag);
          const totals = productTotals(state.picking?.moves ?? [], state.localQty, result.odoo.productId);
          const newQty = totals.received + result.odoo.qtyDone;
          if (totals.expected > 0 && newQty > totals.expected) {
            beep("error");
            if (hapticEnabled) vibrate([100, 50, 100]);
            if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
            dispatch({ type: "SCAN_VARIANCE", expected: totals.expected, actual: newQty, bags });
            return;
          }

          if (result.odoo.tracking === "serial") scannedSerialIdsRef.current.add(result.odoo.lotId);
          // Check if this scan completes the entire picking for a distinct completion buzz.
          const newLocalQty = addToLines(state.localQty, bags);
          const allComplete = state.picking?.moves.every(
            (m) => m.doneQty + (newLocalQty[m.id] ?? 0) >= m.expectedQty,
          ) ?? false;
          beep("success");
          if (hapticEnabled) vibrate(allComplete ? [100, 50, 100, 50, 200] : 200);
          dispatch({
            type: "SCAN_OK",
            productName: result.odoo.productName,
            weightKg: result.normalised.weightKg,
            batch: result.normalised.batch,
            bagNumber: result.normalised.bagNumber,
            bags,
          });
        } else if (result.outcome === "unknown") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({ type: "SCAN_UNKNOWN", rawBarcode: result.rawBarcode });
        } else if (result.outcome === "not_on_picking") {
          beep("error");
          if (hapticEnabled) vibrate([100, 50, 100]);
          dispatch({
            type: "SCAN_NOT_ON_PICKING",
            category: result.normalised.category,
            batch: result.normalised.batch,
            suggestion: result.suggestion,
          });
        }
      })
      .catch((err: unknown) => {
        beep("error");
        if (hapticEnabled) vibrate([100, 50, 100]);
        if (err instanceof RequestError && err.code === "duplicate_serial") {
          dispatch({ type: "SCAN_ALREADY_RECEIVED", lotName: err.message.match(/Bag (.+?) has/)?.[1] ?? "this bag" });
        } else {
          dispatch({
            type: "SCAN_ERR",
            title: "Scan failed",
            msg: err instanceof Error ? err.message : "Unknown error",
          });
        }
      })
      .finally(() => {
        scanInFlight.current = false;
      });
  });

  async function handleValidate() {
    if (!api || state.phase !== "ready") return;
    dispatch({ type: "VALIDATE_START" });
    try {
      const result = await api.validatePicking(ref, { acceptBackorder: true, scans: state.scannedBags });
      dispatch({
        type: "VALIDATE_OK",
        backorderCreated: result.backorderCreated,
        backorderRef: result.backorderPickingRef,
      });
    } catch (err) {
      dispatch({
        type: "VALIDATE_FAIL",
        msg: err instanceof Error ? err.message : "Validation failed",
      });
    }
  }



  // ── Loading ──
  if (state.phase === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-gray-400">Loading picking…</p>
      </div>
    );
  }

  // ── Load error ──
  if (state.phase === "load_error") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-red-400">Cannot open picking</p>
        <p className="text-sm text-gray-400">{state.errorMsg}</p>
        <button
          onClick={() => void navigate({ to: "/receipt", replace: true })}
          className="rounded-xl bg-gray-800 px-6 py-3 font-semibold active:bg-gray-700"
        >
          Back to picking list
        </button>
      </div>
    );
  }

  // ── Done ──
  if (state.phase === "done") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6 text-center">
        <div className="text-6xl text-green-400">✓</div>
        <div>
          <p className="text-xl font-bold">Picking validated</p>
          <p className="mt-1 font-mono text-gray-300">{ref}</p>
        </div>
        {state.backorder?.created && state.backorder.ref && (
          <div className="rounded-xl bg-yellow-900/40 px-4 py-3 text-sm text-yellow-300">
            Backorder created: {state.backorder.ref}
          </div>
        )}
        <button
          onClick={() => void navigate({ to: "/workflow", replace: true })}
          className="w-full max-w-xs rounded-xl bg-blue-600 py-4 text-lg font-semibold active:bg-blue-700"
        >
          Back to workflow
        </button>
      </div>
    );
  }

  // ── Ready / Validating ──
  const picking = state.picking!;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-800 px-4 py-3">
        <button
          onClick={() => void navigate({ to: "/receipt", replace: true })}
          className="text-sm text-gray-400 active:text-white"
        >
          ←
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm font-semibold">{picking.picking.name}</p>
          <p className="truncate text-xs text-gray-500">
            {pickingStateLabel(picking.picking.state)}
            {picking.picking.partner ? ` · ${picking.picking.partner}` : ""}
          </p>
          {picking.picking.origin && (
            <p className="truncate text-xs text-gray-600">{picking.picking.origin}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              const next = !hapticEnabled;
              setHapticEnabled(next);
              if (next) vibrate(100);
            }}
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${hapticEnabled ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-500"}`}
          >
            Buzz
          </button>
          <span className="rounded-full bg-blue-900/50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
            Receipt
          </span>
        </div>
      </div>

      {/* Receiving location */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-800 bg-gray-900/40 px-4 py-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Receiving into</p>
          <p className="truncate text-sm font-medium text-gray-200">
            {state.destLocation?.name ?? picking.picking.destLocation.name}
          </p>
        </div>
        <button
          onClick={() => {
            setLocationError(null);
            setShowLocationPicker(true);
            void loadLocations();
          }}
          className="shrink-0 rounded-lg bg-gray-800 px-3 py-1.5 text-xs font-semibold text-blue-300 active:bg-gray-700"
        >
          Change Location
        </button>
      </div>

      {/* Stale data warning */}
      {showStaleWarning && (
        <div className="mx-4 mt-2 flex items-center justify-between rounded-xl bg-yellow-900/40 px-3 py-2 text-xs text-yellow-300">
          <span>Picking data may be out of date — reload if in doubt.</span>
          <button onClick={() => setShowStaleWarning(false)} className="ml-3 text-yellow-500 active:text-yellow-200">✕</button>
        </div>
      )}

      {/* Picking error (validate failed) */}
      {state.errorMsg && (
        <div className="mx-4 mt-3 rounded-xl bg-red-900/40 px-4 py-3 text-sm text-red-300">
          <p className="font-semibold">Validation failed — tap Retry to try again.</p>
          <p className="mt-1 text-xs text-red-400">{state.errorMsg}</p>
        </div>
      )}

      {/* Progress bar */}
      {(() => {
        const totalExpected = picking.moves.reduce((s, m) => s + m.expectedQty, 0);
        const totalDone = picking.moves.reduce((s, m) => s + m.doneQty + (state.localQty[m.id] ?? 0), 0);
        const pct = totalExpected > 0 ? Math.min(100, (totalDone / totalExpected) * 100) : 0;
        const complete = totalDone >= totalExpected;
        return (
          <div className="px-4 pb-2 pt-3">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-gray-500">Progress</span>
              <span className={`font-semibold tabular-nums ${complete ? "text-green-400" : "text-white"}`}>
                {totalDone} / {totalExpected} bags
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-300 ${complete ? "bg-green-500" : "bg-blue-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Move lines table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-900">
            <tr className="border-b border-gray-800 text-xs uppercase tracking-widest text-gray-500">
              <th className="py-2 pl-4 text-left font-normal">Product</th>
              <th className="py-2 pr-3 text-right font-normal">Exp</th>
              <th className="py-2 pr-4 text-right font-normal">Done</th>
            </tr>
          </thead>
          <tbody>
            {picking.moves.map((move) => {
              const localQty = state.localQty[move.id] ?? 0;
              const done = move.doneQty + localQty;
              const complete = done >= move.expectedQty;
              return (
                <tr key={move.id} className="border-b border-gray-800/60">
                  <td className="py-3 pl-4 text-sm leading-tight">{move.product.name}</td>
                  <td className="py-3 pr-3 text-right tabular-nums text-gray-400">
                    {move.expectedQty}
                  </td>
                  <td className={`py-3 pr-4 text-right tabular-nums ${complete ? "font-semibold text-green-400" : "text-white"}`}>
                    {done}
                    {localQty > 0 && (
                      <span className="ml-1 text-xs font-normal text-blue-400">+{localQty}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Scan feedback panel */}
      <div className="border-t border-gray-800 p-4">
        <div
          key={state.scanSeq}
          className={`rounded-2xl p-4 text-center transition-colors ${
            state.feedback?.kind === "ok"
              ? "bg-scan-ok/20 animate-flash"
              : state.feedback?.kind === "err"
                ? "bg-scan-error/20 animate-flash"
                : "bg-gray-900"
          }`}
        >
          {state.feedback?.kind === "ok" ? (
            <>
              <p className="text-2xl font-bold text-scan-ok">
                ✓ {state.feedback.weightKg} kg
              </p>
              <p className="mt-0.5 text-sm text-gray-300">{state.feedback.productName}</p>
              <p className="text-xs text-gray-500">
                Batch {state.feedback.batch}
                {state.feedback.bagNumber ? ` · Bag ${state.feedback.bagNumber}` : ""}
              </p>
            </>
          ) : state.feedback?.kind === "err" ? (
            <p className="font-semibold text-scan-error">{state.feedback.title}</p>
          ) : (
            <p className="text-sm text-gray-500">Ready to scan</p>
          )}
          {state.scannedBags.length > 0 && (
            <p className="mt-2 text-xs text-gray-600">
              {bagTotal(state.scannedBags)} bag{bagTotal(state.scannedBags) !== 1 ? "s" : ""} scanned this session
            </p>
          )}
        </div>
      </div>

      {/* Validate button */}
      <div className="px-4 pb-6">
        <button
          onClick={() => setShowManual(true)}
          className="mb-2 w-full text-sm text-gray-400 underline active:text-white"
        >
          Damaged barcode? Add bag manually
        </button>
        <button
          onClick={() => void handleValidate()}
          disabled={state.phase === "validating" || state.scannedBags.length === 0}
          className="w-full rounded-xl bg-green-600 py-4 text-lg font-semibold active:bg-green-700 disabled:opacity-50"
        >
          {state.phase === "validating" ? "Validating…" : state.errorMsg ? "Retry Validation" : "Validate Picking"}
        </button>
      </div>

      {/* Bag-count prompt for a scanned non-serial (lot) product */}
      {pendingCount && (
        <BagCountPrompt
          productName={pendingCount.productName}
          batch={pendingCount.batch}
          bagNumber={pendingCount.bagNumber}
          onConfirm={confirmCount}
          onCancel={() => setPendingCount(null)}
        />
      )}

      {/* Manual bag entry (damaged barcode) */}
      {showManual && (
        <ManualBagForm
          workflow="receipt"
          pickingRef={ref}
          products={picking.moves.map((m) => ({ id: m.product.id, name: m.product.name, tracking: m.product.tracking }))}
          defaultBatch={state.scannedBags[state.scannedBags.length - 1]?.batch}
          defaultProductId={state.scannedBags[state.scannedBags.length - 1]?.productId}
          onAdded={onManualAdded}
          onClose={() => setShowManual(false)}
        />
      )}

      {/* Change-location picker */}
      {showLocationPicker && (
        <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 p-4">
          <div className="flex max-h-[80vh] w-full max-w-sm flex-col rounded-2xl bg-gray-800 p-5">
            <h3 className="text-lg font-bold">Receiving location</h3>
            <p className="mb-3 mt-1 text-sm text-gray-400">Scan a location barcode, or tap one below.</p>
            {locationError && (
              <p className="mb-2 rounded-lg bg-red-900/40 px-3 py-2 text-xs text-red-300">{locationError}</p>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-700">
              {locationsLoading ? (
                <p className="p-4 text-center text-sm text-gray-500">Loading locations…</p>
              ) : locations.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">No locations found — scan a location barcode.</p>
              ) : (
                locations.map((loc) => {
                  const selected = (state.destLocation?.id ?? picking.picking.destLocation.id) === loc.id;
                  return (
                    <button
                      key={loc.id}
                      onClick={() => {
                        dispatch({ type: "SET_LOCATION", location: { id: loc.id, name: loc.name } });
                        setShowLocationPicker(false);
                        setLocationError(null);
                      }}
                      className={`flex w-full items-center justify-between gap-2 border-b border-gray-700/60 px-4 py-3 text-left active:bg-gray-700 ${selected ? "bg-gray-700/50" : ""}`}
                    >
                      <span className="min-w-0 truncate text-sm">{loc.name}</span>
                      <span className="ml-2 shrink-0 font-mono text-xs text-gray-500">{loc.barcode}</span>
                    </button>
                  );
                })
              )}
            </div>
            <button
              onClick={() => setShowLocationPicker(false)}
              className="mt-4 w-full rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Exception modal */}
      {state.modal && <ExceptionModal modal={state.modal} onDismiss={() => dispatch({ type: "DISMISS" })} />}

      {/* Leave confirmation — shown when operator tries to navigate away with unsaved scans */}
      {leave.status === "blocked" && (
        <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
            <h3 className="mb-2 text-lg font-bold text-red-400">Leave picking?</h3>
            <p className="mb-5 text-sm text-gray-400">
              You have scanned <span className="font-semibold text-white">{bagTotal(state.scannedBags)} bag{bagTotal(state.scannedBags) !== 1 ? "s" : ""}</span> that have not been saved.
              Leaving now will lose all scans for this session.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => leave.reset()}
                className="flex-1 rounded-xl bg-gray-700 py-3 font-semibold active:bg-gray-600"
              >
                Keep scanning
              </button>
              <button
                onClick={() => leave.proceed()}
                className="flex-1 rounded-xl bg-red-700 py-3 font-semibold active:bg-red-600"
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Exception modal ─────────────────────────────────────────────────────────

interface ExceptionModalProps {
  modal: Modal;
  onDismiss: () => void;
}

function ExceptionModal({ modal, onDismiss }: ExceptionModalProps) {
  let title: string;
  let body: ReactNode;

  if (modal.kind === "unknown") {
    title = "Unknown Barcode";
    body = (
      <>
        <p className="text-gray-300">This barcode was not recognised and has been flagged for review.</p>
        <p className="mt-2 break-all font-mono text-xs text-gray-500">{modal.rawBarcode}</p>
        <p className="mt-1 font-mono text-xs text-gray-600">
          {/* eslint-disable-next-line no-control-regex */}
          {modal.rawBarcode.length} chars · {(modal.rawBarcode.match(/\x1d/g) ?? []).length} GS
        </p>
      </>
    );
  } else if (modal.kind === "not_on_picking") {
    title = "Not on Picking";
    body = (
      <>
        <p className="text-gray-300">
          <span className="font-semibold capitalize">{modal.category.replace(/_/g, " ")}</span>{" "}
          batch <span className="font-mono">{modal.batch}</span> is not expected on this picking.
        </p>
        {modal.suggestion && (
          <p className="mt-2 text-sm text-gray-400">
            Closest match: <span className="font-semibold text-gray-200">{modal.suggestion.productName}</span>. Ask your supervisor to update the purchase order if this is correct.
          </p>
        )}
      </>
    );
  } else if (modal.kind === "already_scanned") {
    title = "Already Scanned";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        scanned on this picking. Skip it and move to the next bag.
      </p>
    );
  } else if (modal.kind === "already_received") {
    title = "Already in Stock";
    body = (
      <p className="text-gray-300">
        Bag <span className="font-mono font-semibold">{modal.lotName}</span> has already been
        received into stock. Each bag can only be scanned once — check the label and try a
        different bag.
      </p>
    );
  } else if (modal.kind === "variance") {
    title = "Extra Bag";
    body = (
      <div className="space-y-2 text-gray-300">
        <p>
          You&apos;ve scanned <span className="font-semibold">{String(modal.actual)}</span> bags
          but only <span className="font-semibold">{String(modal.expected)}</span> were expected.
        </p>
        <p className="text-sm text-gray-400">
          The extra bag has been recorded. Let your supervisor know if this wasn&apos;t intentional.
        </p>
      </div>
    );
  } else {
    title = modal.title;
    body = <p className="text-sm text-gray-400">{modal.message}</p>;
  }

  return (
    <div className="fixed inset-0 flex items-end justify-center bg-black/70 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-gray-800 p-6">
        <h3 className="mb-3 text-lg font-bold text-scan-error">{title}</h3>
        <div className="mb-5">{body}</div>
        <button
          onClick={onDismiss}
          className="w-full rounded-xl bg-blue-600 py-3 font-semibold active:bg-blue-700"
        >
          Continue scanning
        </button>
      </div>
    </div>
  );
}
